/**
 * kkdKuraliService.js — KKD (kişisel koruyucu donanım) kalem kataloğu ve fuar başına KKD kuralı.
 * Backlog #374 (backend #373, V84__13_10_kkd.sql); Fuar Kuralları › KKD ve Global Kurallar › KKD bunu kullanır.
 *
 *   kkd_kalemi       katalog: Yelek, Baret, Ayakkabı, ... (silinmez, pasifleştirilir; pasif kalem HER fuarda bir anda düşer)
 *   fuar_kkd_kurali  bir fuarın kalem başına seviyesi: 0 kapalı (istenmiyor) / 1 uyarır / 2 engeller
 *
 * Satır yoksa seviye 0'dır: kuralı hiç yazılmamış fuar hiçbir kalemi istemez. `fuarId = 0` GLOBAL ŞABLONDUR ve
 * KOPYALANIR (GET 0 → fuara PUT), canlı miras değildir. Okuma her oturuma açık, yazma yalnızca SAKA personeline (firma 403).
 * Mock katmanı yok: uçlar backend'de hazır.
 *
 * ⚠️ PUT /{fuarId}/kural/kkd OTORİTERDİR — gövdede olmayan her aktif kalem 0 olur. Bu yüzden `setFuarKkdKurali` her zaman
 *    TÜM kalemleri gönderir. ⚠️ Kalem adı yalnızca Türkçe (`ad`); İngilizce ad için backend'de metin tablosu yok.
 */
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';

const BASE = `${JAVA_BACKEND_API_URL}/fuar`;

/** fuar_kkd_kurali.seviye (backend KkdConstant). */
export const KKD_SEVIYE = { KAPALI: 0, UYARI: 1, ENGELLER: 2 };

/** Global şablon yuvası: `fuarId = 0`. */
export const KKD_GLOBAL_FUAR_ID = 0;

// Backend 400/409/500'ü bazen düz metin, bazen {message,...} zarfı döner; 409'da zarf, çakışan kodu `data`da taşır.
function parseError(error) {
  const govde = error.response?.data;
  const message = !error.response
    ? 'Sunucu yanıt vermedi.'
    : typeof govde === 'string' && govde
      ? govde
      : govde?.message ?? error.message ?? 'İşlem başarısız oldu.';
  const wrapped = new Error(message);
  wrapped.status = error.response?.status ?? null;
  wrapped.data = govde?.data ?? null;
  throw wrapped;
}

/** Yazma yanıtı `{message, result, data}`; başarısızlık bazen HTTP 200 + `result:false` gelir. */
function unwrap(payload) {
  if (payload && typeof payload === 'object' && !Array.isArray(payload) && 'result' in payload) {
    if (payload.result === false) {
      const err = new Error(payload.message || 'İşlem tamamlanamadı.');
      err.status = 200;
      err.data = payload.data ?? null;
      throw err;
    }
    return payload.data ?? payload;
  }
  return payload;
}

/**
 * Katalog, düşük `sira` önce. `includeInactive` pasifleri de getirir (yönetim ekranı).
 * Şekil: `[{ kkdKalemiId, kod, ad, ikon (lucide adı|null), sira, aktif, fuarSayisi }]`; `fuarSayisi` = kalemi isteyen fuar sayısı
 * (şablon hariç).
 * GET /api/v1/fuar/kkd-kalemleri?includeInactive=
 */
export async function listKkdKalemleri({ includeInactive = false } = {}) {
  try {
    const res = await axios.get(`${BASE}/kkd-kalemleri`, {
      params: { includeInactive: Boolean(includeInactive) }, headers: getAuthHeader(),
    });
    return Array.isArray(res.data) ? res.data : [];
  } catch (e) { parseError(e); }
}

/**
 * Yeni kalem. `kod` verilmezse addan üretilir ve SONRADAN DEĞİŞMEZ. 201; 409 kod zaten var (pasifler dahil); 400 ad yok /
 * ikon biçimsiz. `ikon` bir lucide adı (yalnızca harf/rakam).
 * POST /api/v1/fuar/kkd-kalemleri
 */
export async function createKkdKalemi({ ad, ikon, sira, kod } = {}) {
  const trimOrNull = (v) => (v == null || String(v).trim() === '' ? null : String(v).trim());
  const payload = { ad: trimOrNull(ad), ikon: trimOrNull(ikon) };
  if (trimOrNull(kod)) payload.kod = trimOrNull(kod);
  if (sira !== '' && sira != null) payload.sira = Number(sira);
  try {
    const res = await axios.post(`${BASE}/kkd-kalemleri`, payload, { headers: getAuthHeader() });
    return unwrap(res.data);
  } catch (e) { parseError(e); }
}

/**
 * Kalem güncelle — yalnızca verilen anahtarlar gider: `ad`, `ikon` (null/boş temizler), `sira`, `aktif`. `kod` değişmez, silme yok:
 * `{ aktif: false }` kalemi HER fuarda aynı anda emekliye ayırır, `{ aktif: true }` geri getirir.
 * PUT /api/v1/fuar/kkd-kalemleri/{id}
 */
export async function updateKkdKalemi(id, patch = {}) {
  const payload = {};
  if ('ad' in patch) payload.ad = patch.ad == null || String(patch.ad).trim() === '' ? null : String(patch.ad).trim();
  if ('ikon' in patch) payload.ikon = patch.ikon == null || String(patch.ikon).trim() === '' ? null : String(patch.ikon).trim();
  // Boş sıra "değiştirme" demek — null göndermek 400.
  if (patch.sira !== '' && patch.sira != null) payload.sira = Number(patch.sira);
  if ('aktif' in patch) payload.aktif = Boolean(patch.aktif);
  try {
    const res = await axios.put(`${BASE}/kkd-kalemleri/${id}`, payload, { headers: getAuthHeader() });
    return unwrap(res.data);
  } catch (e) { parseError(e); }
}

/**
 * Bir fuarın KKD kuralı (0 = global şablon). HER aktif kalem döner, satırı olmayan 0 ile. Hiç yapılandırılmamış fuar da 404 vermez.
 * Şekil (zarfsız düz nesne): `{ fuarId, globalSablon, istenen, kalemler: [{ kkdKalemiId, kod, ad, ikon, sira, seviye 0|1|2,
 *   seviyeAdi KAPALI|UYARI|ENGELLER, configured, updateDate, updatedBy }] }`.
 * GET /api/v1/fuar/{fuarId}/kural/kkd
 */
export async function getFuarKkdKurali(fuarId) {
  try {
    const res = await axios.get(`${BASE}/${fuarId}/kural/kkd`, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * Bir fuarın KKD kuralını yazar (0 = global şablon). `seviyeler` = `{ [kkdKalemiId]: 0|1|2 }`; OTORİTER olduğu için çağıran TÜM aktif
 * kalemleri vermeli (verilmeyen 0 olur). Dönen: yazımdan sonraki kural (GET ile aynı şekil).
 * PUT /api/v1/fuar/{fuarId}/kural/kkd
 */
export async function setFuarKkdKurali(fuarId, seviyeler) {
  const kalemler = Object.entries(seviyeler || {}).map(([kkdKalemiId, seviye]) => ({
    kkdKalemiId: Number(kkdKalemiId), seviye: Number(seviye),
  }));
  try {
    const res = await axios.put(`${BASE}/${fuarId}/kural/kkd`, { kalemler }, { headers: getAuthHeader() });
    return unwrap(res.data);
  } catch (e) { parseError(e); }
}
